import React, { useEffect, useState } from "react";
import { FindoSpinner, PageLoader } from "@/components/FindoSpinner";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { labelFor } from "@/lib/categories";
import { IMAGES } from "@/lib/images";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import PhotoGallery from "@/components/PhotoGallery";
import { CreditCard, Truck, ShieldCheck, Home, ArrowRight, ArrowLeft, Tag, Store, CalendarDays, BadgeCheck, Palette, Sparkles, PackageSearch, X } from "lucide-react";
import { ShippingPayModal } from "@/components/ShippingPayModal";

export default function Pay() {
  const { declarationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const alreadyConfirmed = searchParams.get("verified") === "1";
  const { t } = useLang();
  const [decl, setDecl] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(null); // null(loading) | "identity" | "choice" | "pay"
  const [delivery, setDelivery] = useState("pickup");
  const [extClicked, setExtClicked] = useState(false);
  const [shipOpen, setShipOpen] = useState(false);
  const [idConfirmed, setIdConfirmed] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    api(`/declarations/${declarationId}/public`, { auth: false }).then(setDecl).catch(() => setNotFound(true));
  }, [declarationId]);

  // Decide the entry step once the declaration is loaded.
  useEffect(() => {
    if (!decl || step !== null) return;
    const paid = ["paid", "shipping_requested", "shipped", "restituted"].includes(decl.status);
    // Resume: client already opened the partner's external link → land straight
    // on the waiting screen (no identity / choice replay).
    if (decl.payment_mode === "external" && decl.external_recovery_clicked
        && !decl.is_recovery_paid && decl.delivery_method) {
      setDelivery(decl.delivery_method);
      setExtClicked(true);
      setStep("pay");
      return;
    }
    // Identity + verification gate: the object-identification fiche AND the
    // partner-approved identity verification live on /verifier. If the owner
    // hasn't been through it (recovery_identity_confirmed persisted after the
    // partner approves), send them there — wherever they clicked "Récupérer mon
    // objet" (email, bell, tracking…), the flow is identical.
    const idOk = !!decl.recovery_identity_confirmed || alreadyConfirmed;
    setIdConfirmed(idOk || paid);
    if (!idOk && decl.matched && !paid) { navigate(`/verifier/${declarationId}`, { replace: true }); return; }
    if (decl.delivery_method && !paid) setDelivery(decl.delivery_method);
    setStep("choice");
  }, [decl]); // eslint-disable-line

  // Recovery already settled + shipping still due → open the shipping-address
  // confirmation modal directly (the /ship page has been replaced by this modal).
  useEffect(() => {
    if (decl && decl.awaiting_shipping_payment) setShipOpen(true);
  }, [decl?.awaiting_shipping_payment]); // eslint-disable-line

  // After the client opened the partner's external recovery link, poll for the
  // partner's manual confirmation, then auto-route to the next step.
  useEffect(() => {
    if (step !== "pay" || !extClicked) return;
    const id = setInterval(async () => {
      try {
        const d = await api(`/declarations/${declarationId}/public`, { auth: false });
        setDecl(d);
        if (d.is_recovery_paid) {
          clearInterval(id);
          if (delivery === "shipping") setShipOpen(true);
          else window.location.href = `/track/${declarationId}`;
        }
      } catch { /* keep polling */ }
    }, 6000);
    return () => clearInterval(id);
  }, [step, extClicked, delivery, declarationId]);

  async function chooseDelivery(method) {
    setDelivery(method);
    try {
      await api(`/declarations/${declarationId}/delivery-choice`, { method: "POST", auth: false, body: { method } });
    } catch { /* non-blocking */ }
    setStep("pay");
  }

  async function payFree() {
    setBusy(true);
    try {
      await api(`/declarations/${declarationId}/recovery-free`, { method: "POST", auth: false, body: { method: delivery } });
      if (delivery === "shipping") { setShipOpen(true); setBusy(false); }
      else window.location.href = `/track/${declarationId}`;
    } catch (e) { toast.error(e.message); setBusy(false); }
  }

  async function payExternal() {
    const url = decl.external_recovery_url;
    if (!url) { toast.error(t("pub.payLinkUnavailable")); return; }
    setBusy(true);
    try {
      api(`/declarations/${declarationId}/external-click`, { method: "POST", auth: false }).catch(() => {});
      window.open(url, "_blank", "noopener");
      setExtClicked(true);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  // R4: once the partner confirmed the external recovery payment, the link must
  // not be reopened — inform the client and move to the next step.
  const recoveryConfirmed = !!(decl && (decl.is_recovery_paid || decl.external_recovery_confirmed));
  function reopenExternal() {
    if (recoveryConfirmed) {
      toast.success(t("hc.paymentAlreadyConfirmedByThe"));
      if (delivery === "shipping") setShipOpen(true);
      else window.location.href = `/track/${declarationId}`;
      return;
    }
    payExternal();
  }

  if (notFound) return <div className="max-w-md mx-auto py-32 text-center text-slate-500">{t("pay.notfound")}</div>;
  if (!decl) return <PageLoader label={t("c.loading")} className="max-w-md mx-auto py-32" />;
  if (step === null) return <div className="max-w-md mx-auto py-32 text-center text-slate-400"><FindoSpinner size={24} className="mx-auto" /></div>;

  const item = decl.item_preview;
  const isExternal = decl.payment_mode === "external";
  const alreadyPaid = ["paid", "shipping_requested", "shipped", "restituted"].includes(decl.status);
  const recoveryFree = !isExternal || Number(decl.partner_fee_eur) === 0;

  return (
    <div className="bg-background">
      <div className="relative h-56 overflow-hidden photo-scrim">
        <img src={IMAGES.conciergeDesk} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6">
          <span className="inline-flex items-center gap-2 rounded-full badge-red px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">{t("pay.badge")}</span>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold">{t("pay.title")}</h1>
          <p className="mt-2 text-white/85 max-w-lg">{t("pay.subtitleWith")} {decl.partner_name}.</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-6 py-12">
        {(idConfirmed || decl.recovery_identity_confirmed || alreadyConfirmed) && !alreadyPaid && (step === "choice" || step === "pay") && (
          <button type="button" onClick={() => setReviewOpen(true)} className="w-full text-left mb-5 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 hover:bg-emerald-100 transition-colors group" data-testid="pay-verified-banner">
            <span className="mt-0.5 h-9 w-9 grid place-items-center rounded-xl bg-emerald-100 text-emerald-700 shrink-0"><ShieldCheck size={18} /></span>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-emerald-800 text-sm">{t("hc.itemAlreadyConfirmed")}</p>
              <p className="text-emerald-700 text-sm mt-0.5">{t("hc.youHaveAlreadyConfirmedThat")} <span className="underline font-semibold">{t("hc.reviewTheItemDetails")}</span></p>
            </div>
            <ArrowRight size={18} className="mt-1 text-emerald-600 shrink-0 transition-transform group-hover:translate-x-0.5" />
          </button>
        )}

        <div className="findo-form mt-2 bg-white rounded-2xl border border-border card-shadow overflow-hidden" data-testid="pay-card">
          {item?.image_base64 && <img src={item.image_base64} alt="" className="w-full h-52 object-cover" />}
          <div className="p-6">
            <p className="text-xs text-findo-indigo font-semibold">{labelFor(decl.category)}</p>
            <p className="font-semibold text-slate-800 mt-1">{item?.description || decl.description}</p>

            {!decl.matched ? (
              <div className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                {t("pay.noMatch")}
              </div>
            ) : alreadyPaid ? (
              <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-center" data-testid="pay-already-paid">
                <div className="mx-auto h-12 w-12 grid place-items-center rounded-full bg-emerald-100 text-emerald-700">
                  <ShieldCheck size={26} />
                </div>
                <p className="mt-3 font-bold text-emerald-800">{t("pub.recoveryAlreadyPaid")}</p>
                <p className="mt-1 text-sm text-emerald-700">{t("pub.noSecondPayment")}</p>
                {decl.delivery_method === "shipping" && decl.is_shipping_paid ? (
                  <button disabled className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-100 text-emerald-700 font-semibold px-5 py-2.5 cursor-default" data-testid="pay-shipping-link">
                    <Truck size={17} /> {t("hc.shippingPaid")}
                  </button>
                ) : decl.awaiting_shipping_payment ? (
                  <button onClick={() => setShipOpen(true)} className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-xl bg-amber-500 text-white font-semibold px-5 py-2.5 hover:bg-amber-600 transition-colors" data-testid="pay-shipping-link">
                    <Truck size={17} /> {t("hc.payTheShippingFees")}
                  </button>
                ) : (
                  <Link to={`/track/${declarationId}`} className="mt-4 inline-flex items-center justify-center rounded-xl bg-emerald-600 text-white font-semibold px-5 py-2.5 hover:bg-emerald-700 transition-colors" data-testid="pay-track-link">
                    {t("hc.trackMyRecovery")}
                  </Link>
                )}
              </div>
            ) : step === "choice" ? (
              <div className="mt-5" data-testid="delivery-choice">
                <p className="text-sm font-bold text-slate-700 mb-3">{t("pay.howRecover")}</p>
                <div className="grid grid-cols-1 gap-2.5">
                  <button type="button" onClick={() => chooseDelivery("pickup")}
                    className="text-left rounded-xl border-2 border-border hover:border-findo-indigo hover:bg-findo-indigo/[0.03] p-4 transition-colors flex items-center gap-3"
                    data-testid="delivery-pickup">
                    <span className="h-10 w-10 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo shrink-0"><Home size={20} /></span>
                    <span className="font-semibold text-slate-800">{t("pay.pickup")}</span>
                    <ArrowRight size={18} className="ml-auto text-slate-300" />
                  </button>
                  {item?.shipping_available && (
                    <button type="button" onClick={() => chooseDelivery("shipping")}
                      className="text-left rounded-xl border-2 border-border hover:border-findo-indigo hover:bg-findo-indigo/[0.03] p-4 transition-colors flex items-center gap-3"
                      data-testid="delivery-shipping">
                      <span className="h-10 w-10 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo shrink-0"><Truck size={20} /></span>
                      <span className="font-semibold text-slate-800">{t("pay.ship")}</span>
                      <ArrowRight size={18} className="ml-auto text-slate-300" />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-5" data-testid="pay-step">
                <button type="button" onClick={() => { setStep("choice"); setExtClicked(false); }}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-findo-indigo mb-4" data-testid="pay-back-btn">
                  <ArrowLeft size={14} /> {t("hc.changeMethod")}
                </button>
                <p className="text-sm text-slate-500 mb-4 flex items-center gap-1.5">
                  {delivery === "shipping" ? <Truck size={15} className="text-findo-indigo" /> : <Home size={15} className="text-findo-indigo" />}
                  {delivery === "shipping" ? t("pay.ship") : t("pay.pickup")}
                </p>

                {extClicked ? (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-5 text-center" data-testid="pay-waiting">
                    <div className="flex justify-center"><FindoSpinner size={44} /></div>
                    <p className="mt-3 font-bold text-amber-800">{t("hc.waitingForThePartnerS")}</p>
                    <p className="mt-1 text-sm text-amber-700">
                      {t("hc.asSoonAs")} <b>{decl.partner_name}</b> {t("hc.hasConfirmedYourPaymentYou")}
                      {" "}{delivery === "shipping" ? t("hc.toTheShippingFeePayment") : t("hc.toYourRecoveryTracking")}
                    </p>
                    <Button onClick={reopenExternal} variant="outline" disabled={recoveryConfirmed}
                      className="mt-4 rounded-xl font-semibold disabled:opacity-60" data-testid="pay-reopen-btn">
                      {recoveryConfirmed ? t("hc.paymentAlreadyConfirmedByThe") : t("hc.reopenThePaymentLink")}
                    </Button>
                  </div>
                ) : (
                  <Button onClick={recoveryFree ? payFree : payExternal} disabled={busy}
                    className="w-full h-12 rounded-xl font-semibold text-base"
                    data-testid={recoveryFree ? "pay-free-btn" : "pay-external-btn"}>
                    {busy ? <FindoSpinner size={16} /> : <><CreditCard size={18} className="mr-2" /> {t("hc.payTheRecoveryFees")}</>}
                  </Button>
                )}
              </div>
            )}

            <p className="mt-4 text-xs text-slate-400 flex items-center justify-center gap-1.5"><ShieldCheck size={13} /> {t("pay.secure")}</p>
          </div>
        </div>
      </div>
      <ShippingPayModal declarationId={declarationId} open={shipOpen} onClose={() => setShipOpen(false)} />

      {reviewOpen && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-6" onClick={() => setReviewOpen(false)} data-testid="pay-review-fiche">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-2xl animate-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-border">
              <p className="font-display text-lg font-bold text-findo-navy flex items-center gap-2">
                <PackageSearch size={20} className="text-findo-red" /> {t("hc.itemDetails")}
              </p>
              <button type="button" onClick={() => setReviewOpen(false)} className="h-9 w-9 grid place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors" data-testid="pay-review-x" aria-label={t("hc.close")}><X size={20} /></button>
            </div>
            <div className="max-h-[72vh] overflow-y-auto px-6 py-5">
              {(item?.image_base64 || item?.image_base64_2) && (
                <div className="rounded-2xl overflow-hidden bg-slate-100 mb-5">
                  <PhotoGallery photos={[item?.image_base64, item?.image_base64_2]} alt={item?.description || decl.description} testid="pay-review-gallery" />
                </div>
              )}
              <div className="space-y-4">
                <Detail icon={<PackageSearch size={16} />} label="Description" value={item?.description || decl.description} testid="pay-review-description" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Detail icon={<Tag size={16} />} label={t("hc.category")} value={labelFor(decl.category)} testid="pay-review-category" />
                  <Detail icon={<Store size={16} />} label={t("hc.establishment")} value={decl.partner_name} testid="pay-review-partner" />
                  {item?.brand && <Detail icon={<BadgeCheck size={16} />} label={t("hc.brand")} value={item.brand} testid="pay-review-brand" />}
                  {item?.color && <Detail icon={<Palette size={16} />} label={t("hc.color")} value={item.color} testid="pay-review-color" />}
                  {item?.condition && <Detail icon={<Sparkles size={16} />} label={t("hc.condition")} value={item.condition} testid="pay-review-condition" />}
                  {item?.deposit_date && <Detail icon={<CalendarDays size={16} />} label={t("hc.depositedOn")} value={fmtDate(item.deposit_date)} testid="pay-review-date" />}
                </div>
              </div>
              <Button onClick={() => setReviewOpen(false)} className="mt-6 w-full h-11 rounded-xl font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="pay-review-close">
                {t("hc.close")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <span className="mt-0.5 h-9 w-9 grid place-items-center rounded-xl bg-findo-navy/5 text-findo-navy shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
        <p className="text-findo-navy font-medium break-words">{value || "—"}</p>
      </div>
    </div>
  );
}

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(document.documentElement.lang || "fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return s; } };
